// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import * as bankAccountsApi from "../api"
import { readBackendMessage } from "../../../../../../utility/alerts/getErrorMessage"

//Tiempo de vida de las listas de cuentas en el store (15 minutos)
export const BANK_ACCOUNTS_CACHE_TTL = 15 * 60 * 1000

//Listas que se reutilizan del store mientras siga vigente el TTL. Las chequeras quedan
//fuera: gestion las recarga justo después de escribir en otra store y el caché ignoraría esa recarga
const CACHED_LISTS = ['data', 'dataMXN', 'dataMXNKuspit', 'dataUSD']

const initialListMeta = { status: 'idle', error: null, lastFetch: null, isRequested: true, requestId: null }

const asList = (data) => (Array.isArray(data) ? data : [])
const isLatest = (state, action) => state?.requestId === action.meta.requestId

//Texto que createAsyncThunk pone en action.error.message al rechazar con rejectWithValue: no viene del backend
const RTK_REJECTED_SENTINEL = 'Rejected'

//Error de una lectura fallida: el payload del backend o, sin él (caída de red), null para que quien
//lo lea use su propio texto de respaldo en lugar de pintar "Rejected"
const readError = (action) => {
    const message = action.error?.message

    return action.payload ?? (message === RTK_REJECTED_SENTINEL ? null : message) ?? null
}

/**
 * Crea el thunk de lectura de una de las listas del store (`data`, `dataMXN`, ...).
 *
 * Cada lista lleva su propio estado en `listsMeta[key]`: con una sola bandera para
 * todas, la primera que se recargaba la apagaba y las demás se quedaban viejas.
 *
 * Con `cached` la `condition` reutiliza el store mientras siga vigente el TTL, así que
 * un dispatch cortado resuelve sin `payload` (`meta.condition: true`): quien necesite
 * la lista después de pedirla debe leerla del store, no del resultado del dispatch.
 * Sin `cached` cada dispatch sale a la red y gana la última respuesta.
 */
const createListThunk = (type, key, fetchList, { cached }) => createAsyncThunk(type, async (_, { rejectWithValue }) => {
    try {
        return {
            data: await fetchList(),
            fetchedAt: Date.now()
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
}, cached ? {
    condition: (_, { getState }) => {
        const bankAccounts = getState()?.bankAccounts
        const meta = bankAccounts?.listsMeta?.[key]

        if (!meta) return true
        //Invalidación explícita (enableRequest): pasa aunque haya una petición en vuelo,
        //que pudo salir antes de la escritura; su respuesta se descarta por requestId
        if (meta.isRequested) return true
        //Ya hay una petición en vuelo: los demás se cuelgan de esa
        if (meta.status === 'pending') return false
        //Lista vacía
        if (!bankAccounts[key]?.length) return true

        return Date.now() - (meta.lastFetch ?? 0) > BANK_ACCOUNTS_CACHE_TTL
    }
} : undefined)

const addListCases = (builder, thunk, key) => {
    builder.addCase(thunk.pending, (state, action) => {
        //isRequested se consume aquí para que una invalidación durante la petición siga vigente
        state.listsMeta[key] = { ...state.listsMeta[key], status: 'pending', error: null, isRequested: false, requestId: action.meta.requestId }
    })
    builder.addCase(thunk.fulfilled, (state, action) => {
        if (!isLatest(state.listsMeta[key], action)) return
        state[key] = asList(action.payload.data)
        state.listsMeta[key] = { ...state.listsMeta[key], status: 'fulfilled', lastFetch: action.payload.fetchedAt }
    })
    builder.addCase(thunk.rejected, (state, action) => {
        if (!isLatest(state.listsMeta[key], action)) return
        //Permite reintentar en la siguiente llamada sin esperar al TTL
        state.listsMeta[key] = {
            ...state.listsMeta[key],
            status: 'rejected',
            isRequested: true,
            error: readError(action)
        }
    })
}

//return bank accounts MXN from traditional and kuspit
export const getBankAccounts = createListThunk('appBankAccounts/getBankAccounts', 'data', bankAccountsApi.fetchBankAccounts, { cached: true })

// Return bank accounts MXN from traditional
export const getBankAccountsMXN = createListThunk('appBankAccounts/getBankAccountsMXN', 'dataMXN', bankAccountsApi.fetchBankAccountsMXN, { cached: true })

// Return bank accounts MXN from kuspit
export const getBankAccountsMXNKuspit = createListThunk('appBankAccounts/getBankAccountsMXNKuspit', 'dataMXNKuspit', bankAccountsApi.fetchBankAccountsMXNKuspit, { cached: true })

// Return bank accounts USD
export const getBankAccountsUSD = createListThunk('appBankAccounts/getBankAccountsUSD', 'dataUSD', bankAccountsApi.fetchBankAccountsUSD, { cached: true })

//DEPRECATED: Use getBankAccounts instead
// export const getAllBankAccounts = createAsyncThunk('appBankAccounts/getAllBankAccounts', async (_, { getState, dispatch }) => {
//     const state = getState()
//     const cachedData = state.bankAccounts.allAccounts

//     if (state.bankAccounts.allAccounts.length > 0 && !state.bankAccounts.isRequested) {
//         return {
//             data: cachedData
//         }
//     }

//     const response = await axios.get(BANK_ACCOUNTS_ENDPOINT
//     // eslint-disable-next-line no-use-before-define
//     dispatch(disableRequest())

//     return {
//         data: response.data
//     }
// })

export const getBankAccountById = createAsyncThunk('appBankAccounts/getBankAccountById', async (id, { rejectWithValue }) => {
    try {
        return {
            selectedBankAccount: await bankAccountsApi.fetchBankAccountById(id)
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//Recarga la cuenta seleccionada sin vaciarla mientras responde el backend
export const getBankAccountByIdWithoutEraseAccount = createAsyncThunk('appBankAccounts/refreshBankAccountById', async (id, { rejectWithValue }) => {
    try {
        return {
            selectedBankAccount: await bankAccountsApi.fetchBankAccountById(id)
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const getBankAccountCheckbooks = createListThunk('appBankAccounts/getBankAccountCheckbooks', 'checkbooks', bankAccountsApi.fetchBankAccountCheckbooks, { cached: false })

export const getBankAccountCheckBooks = createListThunk('appBankAccounts/getBankAccountCheckBooks', 'checkBooks', bankAccountsApi.fetchBankAccountCheckBooks, { cached: false })

// export const updateBankAccountId = createAsyncThunk('appBankAccounts/updateBankAccountId', async (bankAccount, { dispatch }) => {
//     const response = await axios.put(UPDATE_BANK_ACCOUNTS_STATUS_ENDPOINT.concat(`${bankAccount.bankAccountId}/?status_account=${bankAccount.bankAccountData}`))
//     await dispatch(getBankAccountById(bankAccount.bankAccountId))
//     return response.data
// })

//La lista ya respondió, con datos, vacía o con error. Todas arrancan en [], así que solo con el arreglo
//no se distingue "todavía no llega" de "llegó vacía"; quien la pinta lo usa para decidir si sigue el skeleton
export const selectIsBankAccountsListSettled = (state, key) => {
    const status = state.bankAccounts?.listsMeta?.[key]?.status
    return status === 'fulfilled' || status === 'rejected'
}

export const appBankAccountsSlice = createSlice({
    name: 'appBankAccounts',
    initialState: {
        data: [],
        dataMXN: [],
        dataUSD: [],
        dataMXNKuspit: [],
        // allAccounts: [],
        //Estado del caché de cada lista: { [llave]: { status, error, lastFetch, isRequested, requestId } }
        listsMeta: {
            data: { ...initialListMeta },
            dataMXN: { ...initialListMeta },
            dataMXNKuspit: { ...initialListMeta },
            dataUSD: { ...initialListMeta },
            checkbooks: { ...initialListMeta },
            checkBooks: { ...initialListMeta }
        },
        selectedBankAccount: {},
        selectedBankAccountStatus: 'idle',
        selectedBankAccountError: null,
        //Petición vigente de getBankAccountById, para descartar la respuesta de una cuenta anterior
        selectedBankAccountRequestId: null,
        checkbooks: [],
        checkBooks: []
    },
    reducers: {
        //Invalida las cuatro listas a la vez: una escritura puede cambiar cualquiera de ellas
        enableRequest: (state) => {
            CACHED_LISTS.forEach(key => {
                state.listsMeta[key] = { ...state.listsMeta[key], isRequested: true }
            })
        },
        resetSelectedBankAccount: (state) => {
            state.selectedBankAccount = {}
        }
    },
    extraReducers: builder => {
        addListCases(builder, getBankAccounts, 'data')
        addListCases(builder, getBankAccountsMXN, 'dataMXN')
        addListCases(builder, getBankAccountsMXNKuspit, 'dataMXNKuspit')
        addListCases(builder, getBankAccountsUSD, 'dataUSD')
        addListCases(builder, getBankAccountCheckbooks, 'checkbooks')
        addListCases(builder, getBankAccountCheckBooks, 'checkBooks')
        builder.addCase(getBankAccountById.pending, (state, action) => {
            state.selectedBankAccount = {}
            state.selectedBankAccountStatus = 'loading'
            state.selectedBankAccountError = null
            state.selectedBankAccountRequestId = action.meta.requestId
        })
        builder.addCase(getBankAccountById.fulfilled, (state, action) => {
            if (state.selectedBankAccountRequestId !== action.meta.requestId) return
            // Solo actualizar selectedBankAccount, mantener data intacto
            state.selectedBankAccount = action.payload.selectedBankAccount
            state.selectedBankAccountStatus = 'ready'
            state.selectedBankAccountError = null
        })
        builder.addCase(getBankAccountById.rejected, (state, action) => {
            if (state.selectedBankAccountRequestId !== action.meta.requestId) return
            state.selectedBankAccount = {}
            state.selectedBankAccountStatus = 'error'
            //AccountLoadError pinta el detalle como texto: guardar el payload (un objeto) rompería el render
            state.selectedBankAccountError = readBackendMessage(action.payload)
        })
        builder.addCase(getBankAccountByIdWithoutEraseAccount.fulfilled, (state, action) => {
            //Si mientras tanto se pidió otra cuenta (o la misma con getBankAccountById), esta
            //respuesta ya no describe la cuenta en pantalla
            if (String(state.selectedBankAccount?.id) !== String(action.meta.arg)) return
            state.selectedBankAccount = action.payload.selectedBankAccount
            state.selectedBankAccountStatus = 'ready'
            state.selectedBankAccountError = null
        })
        // builder.addCase(getAllBankAccounts.fulfilled, (state, action) => {
        //     state.allAccounts = action.payload.data
        // })
    }
})

export const { enableRequest, resetSelectedBankAccount } = appBankAccountsSlice.actions


export const addBankAccount = createAsyncThunk('appBankAccounts/addBankAccount', async (bankAccount, { dispatch, rejectWithValue }) => {
    try {
        const data = await bankAccountsApi.createBankAccount(bankAccount)
        //No se hace `unwrap`: si la recarga falla, el alta sigue siendo un éxito
        dispatch(enableRequest())
        await dispatch(getBankAccounts())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const updateBankAccount = createAsyncThunk('appBankAccounts/updateBankAccount', async (bankAccount, { dispatch, rejectWithValue }) => {
    try {
        const data = await bankAccountsApi.updateBankAccount(bankAccount.bankAccountId, bankAccount.bankAccountData)
        dispatch(enableRequest())
        await dispatch(getBankAccounts())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const updateManagerBankAccount = createAsyncThunk('appBankAccounts/updateManagerBankAccount', async (bankAccount, { dispatch, rejectWithValue }) => {
    try {
        const data = await bankAccountsApi.updateManagerBankAccount(bankAccount.id, bankAccount)
        dispatch(enableRequest())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const hideBankAccount = createAsyncThunk('appBankAccounts/hideBankAccount', async (bankAccount, { dispatch, rejectWithValue }) => {
    try {
        const data = await bankAccountsApi.hideBankAccount(bankAccount.bankAccountId)
        dispatch(enableRequest())
        await dispatch(getBankAccounts())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const addInvestment = createAsyncThunk('appBankAccounts/addInvestment', async (bankAccount, { dispatch, rejectWithValue }) => {
    try {
        const data = await bankAccountsApi.createInvestment(bankAccount)
        dispatch(enableRequest())
        await dispatch(getBankAccounts())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export default appBankAccountsSlice.reducer
